import { z } from 'zod';
import type { $output } from 'zod/v4/core';

import type { SourcePos } from './ast.ts';
import type { EvalContext } from './EvalContext.ts';
import type { Env, EvalResult } from './env.ts';
import type { ExprError } from './errors.ts';
import type { AstInput, OpCall, Spans } from './normalize.ts';
import type { ColumnType, Schema } from './schema/types.ts';
import type { Dataset, LazyDataset, MaterializedDataset } from './value.ts';

/**
 * A row function: takes a row record (column-name → value) and produces a
 * single value. Built by `compileExpr` and consumed by arquero verbs
 * (filter predicates, derive bodies, etc.).
 */
export type RowFn = (row: Record<string, unknown>) => unknown;

/**
 * Compile callback handed to each op's `compileExpr` method. Recursively
 * compiles a child AST to a `RowFn`. The `path` argument anchors errors
 * thrown during compilation to a source position.
 *
 * Typed with `node: unknown` (rather than `AstInput`) because the stored
 * compileExpr signature in `EagerOpMeta` cannot reference recursive types
 * — Zod 4's `$replace` walker blows past TypeScript's instantiation depth
 * limit when it encounters `AstInput`'s self-reference. Callers pass
 * `AstInput` values; the widening only affects the type seen inside
 * `compileExpr` bodies.
 */
export type CompileFn = (node: unknown, path: string) => RowFn;
/**
 * Type-inference callback handed to each op's `inferType` method.
 * Recursively walks an expression-context child AST and returns its
 * inferred `ColumnType` against the surrounding dataset's Schema and
 * the current lexical env. Mirror of `CompileFn` for the schema /
 * type-check pass.
 *
 * Typed with `node: unknown` for the same reason as `CompileFn` — the
 * stored signature in `EagerOpMeta` can't reference `AstInput`'s
 * recursive shape without exceeding TypeScript's instantiation-depth
 * limit. Callers pass `AstInput` values; the widening only affects
 * the type seen inside `inferType` bodies.
 *
 * `schemaOverride` lets a caller infer against a schema other than the
 * one `runPropagateSchema` closed over — used by `derive`'s
 * `propagateSchema` to fold an accumulating schema so a later pair
 * referencing an earlier-derived column is precisely typed. Omitted ⇒
 * the surrounding dataset's input schema is used.
 */
export type InferFn = (node: unknown, path: string, schemaOverride?: Schema) => ColumnType;

/**
 * Non-recursive variant of `OpCall` used in the registry-stored
 * `compileExpr` signature. Authors writing `compileExpr` see `call.params`
 * typed as `readonly unknown[]` (rather than `readonly AstInput[]`) — pass
 * each element to `compile` to convert it to a `RowFn`. The narrower
 * `OpCall` type is recovered at any site that needs to inspect the AST
 * structure further (today, all such callers go through `compileArgs` /
 * `compileChained` in `builtins.ts`).
 */
export type RawOpCall = {
  readonly name: string;
  readonly params?: readonly unknown[];
  readonly [kwarg: string]: unknown;
};

/**
 * Fields shared by every special-form context — the path of the
 * call being evaluated, the source-position sidecar, and the
 * conversation-scoped `EvalContext`. Adding a new per-evaluation
 * field that both top-level special ops *and* special pipeline
 * verbs should see is a single edit here.
 */
export type BaseSpecialCtx = {
  readonly path: string;
  readonly spans: Spans;
  readonly evalCtx: EvalContext;
  /**
   * The lexical environment active where the special form / verb is
   * being evaluated. Top-level special ops always have it (it is the
   * call's own env); special pipeline verbs see the `transform`-level
   * env. Surfaced here (rather than only on `SpecialCtx`) so the
   * shared set-reference resolver (`predicate-refs.ts`), invoked from
   * both the qkg op and the `filter` verb's plan-replay seam, can
   * resolve `(col …)` Form-1 bindings through `Env` → `Session`.
   */
  readonly env: Env;
  /**
   * Force materialisation of a `LazyDataset`. Supplied via callback
   * (not direct import) so special ops/verbs can route through the
   * materializer without `builtins.ts` taking a runtime dependency on
   * `materialize.ts` (which would close a load-order cycle through
   * `evaluator.ts`). The evaluator binds this in `buildSpecialCtx` to
   * the surrounding script's `ops`/`verbs` so plan-replay verb
   * dispatch sees the right registries. Shared here so the
   * set-reference resolver can materialise a referenced column from
   * either the qkg op or the `filter` verb seam.
   */
  readonly materialize: (ds: LazyDataset) => Promise<MaterializedDataset>;
  /**
   * Op and verb tables active in the current `evalScript` invocation.
   * Special-form `apply` handlers that re-enter the engine (e.g.
   * `(materialize ds)` re-running plan verbs at materialisation time)
   * MUST pass these through so custom extension ops/verbs registered
   * for this script are visible to the inner evaluation. Echoing
   * `EvalState.ops` / `EvalState.verbs` from the surrounding evaluator
   * — `buildSpecialCtx` populates them.
   */
  readonly ops: NameTable;
  readonly verbs: NameTable;
  /**
   * Set by `liftPerCell` (and only there) while a verb's `apply` is
   * being re-entered per cell of a `nested-dataset` axis: the OUTER
   * column name currently being lifted. `undefined` ⇔ not inside a
   * per-cell lift (row-level dispatch). Verbs use it both to detect
   * the per-cell form and to name per-axis outputs — e.g.
   * `time-bucket` emits `<liftColumn>-<agg>` columns (PHASE_4_PREP.md
   * D11.1/D11.2). Threaded from `EvalState.liftColumn` by
   * `buildSpecialCtx`.
   */
  readonly liftColumn?: string;
};

/**
 * Context handed to special-form `apply` handlers. They control evaluation
 * of their own children — the dispatcher does not pre-evaluate args for
 * special ops. Shares `path`/`spans`/`evalCtx`/`env`/`materialize` with
 * `SpecialVerbCtx` via `BaseSpecialCtx`; the recursive `evalNode` /
 * `evalVerb` callbacks are specific to top-level special forms.
 */
export type SpecialCtx = BaseSpecialCtx & {
  readonly evalNode: (node: AstInput, env: Env, path: string) => Promise<EvalResult>;
  readonly evalVerb: (call: OpCall, dataset: Dataset, path: string, sourceBindingName?: string) => Promise<Dataset>;
};

/**
 * Context handed to special pipeline-verb `apply` handlers (filter,
 * derive). Shares `path`/`spans`/`evalCtx` with `SpecialCtx` via
 * `BaseSpecialCtx`; adds the `compile` callback that walks
 * expression-context children into per-row functions.
 */
export type SpecialVerbCtx = BaseSpecialCtx & {
  readonly compile: CompileFn;
};
/**
 * Context handed to a pipeline verb's `propagateSchema` method (§5.5).
 * Provides the input dataset's Schema, the verb call's path (for
 * sub-path computation against expression children), and a recursive
 * `inferExprType` walker for verbs (like `derive`) whose args include
 * expression-context sub-trees.
 *
 * `propagateSchema` is independent of `apply` — the engine computes
 * the propagated Schema and then assigns it to the verb's output
 * Dataset. Verbs do not produce a Schema themselves.
 */
export type PropagateSchemaCtx = {
  readonly inputSchema: Schema;
  readonly path: string;
  readonly inferExprType: InferFn;
  /**
   * The OUTER column name being lifted, when `propagateSchema` runs
   * inside a per-cell lift (`inputSchema` is then the cell's inner
   * schema). `undefined` ⇔ row-level propagation (`inputSchema` is
   * the threaded dataset's schema). Mirrors `BaseSpecialCtx.liftColumn`
   * so a verb's `propagateSchema` and `apply` agree on the form;
   * threaded by `runPropagateSchema` (PHASE_4_PREP.md D11.1/D11.2).
   */
  readonly liftColumn?: string;
};
/**
 * Context handed to a pipeline verb's `validateArgs` method (§5.8).
 * Provides the running input schema, the op table (needed by walkers
 * that recurse into op-call children of expression-context args), the
 * spans sidecar, and the verb call's path for sub-path computation
 * against arg children.
 *
 * `validateArgs` returns `readonly ExprError[]` rather than throwing
 * so the type-check pass can aggregate diagnostics across every verb
 * in a `(transform …)` pipeline.
 */
export type ValidateArgsCtx = {
  readonly inputSchema: Schema;
  readonly ops: NameTable;
  readonly spans: Spans;
  readonly path: string;
};

/**
 * A single column-name USAGE inside a verb's args (definitions are
 * excluded — e.g. derive's new-column LHS, rename's target name).
 * Verbs declare these via `BaseVerbMeta.referencedColumns`; the engine
 * uses the list as the single source of truth for:
 *
 *   1. Unknown-column-ref diagnostics in the §5.8 type-check pass.
 *   2. Per-cell dispatch axis detection in `evalVerbCall` (Phase 4-prep
 *      sub-task 4-prep.3): a ref into a `nested-dataset` column triggers
 *      automatic per-cell lifting of the verb.
 *   3. (Future) Push-down optimiser pattern-matching on the ref set.
 *
 * `path` keys into the spans sidecar — same convention `nodePos(spans, path)`
 * consumes — so each ref carries enough information to attribute errors and
 * diagnostics to the precise source position.
 *
 * `colLabels` (optional) marks a `(col …)` *expression-context* ref: the
 * raw label tuple of a `(col a b c …)` node where exactly one label is
 * the column and the rest are an ignored binding prefix / struct
 * sub-path (the role-based `pickColumnPath` rule, SSOT in
 * `column-ref.ts`). `validateRefs` resolves it schema-aware (valid iff
 * *some* label is a schema column) instead of the plain `schema.has(name)`
 * check; `name` carries the rendered tuple for the diagnostic message.
 * Such a ref is **never** a per-cell lift axis — `(col …)` in an
 * expression body is a per-row accessor on the current row, not a
 * nested-dataset column — so `evalVerbCall`'s axis filter excludes it.
 */
export type ColumnRefInArg = {
  readonly name: string;
  readonly path: string;
  readonly colLabels?: readonly string[];
};

export type Effect = 'pure' | 'io';

/**
 * One worked example of an op call. `expr` is the s-expression the LLM
 * can copy verbatim — it must `parse` + `normalize` cleanly (enforced
 * by `op-examples.test.ts`). `result`, when present, is the evaluated
 * value for a pure scalar op; the doc renderer prints it as prose
 * *adjacent to* the code fence (e.g. `→ 6`), never inside it, so the
 * fenced block stays pure, copy-pasteable DSL.
 */
export type OpExampleItem = {
  readonly expr: string;
  readonly result?: string;
};

/**
 * The `example` doc field: a single worked example, or an ordered list
 * of alternatives (e.g. the three `(col …)` call shapes). The doc
 * renderer owns *all* formatting — arrows, fences, ordering — so
 * authors never hand-format separators or result arrows into a string.
 */
export type OpExample = OpExampleItem | readonly OpExampleItem[];

/**
 * Normalise the structured `example` field to an ordered item list.
 * SSOT for the single-or-array shape — the doc renderer and the
 * example round-trip test both consume this rather than re-deriving it.
 */
export const exampleItems = (e: OpExample): readonly OpExampleItem[] =>
  Array.isArray(e) ? (e as readonly OpExampleItem[]) : [e as OpExampleItem];
/**
 * Fields common to every registered op / verb meta — `effect`, an
 * optional structured `example` (see `OpExample`), and an optional `childScopes`
 * declaration enumerating which scopes the op's children dispatch
 * from. Each concrete meta type intersects with this base; the
 * `evaluation` discriminator and `apply` (whose signature varies by
 * kind) stay on the concrete type. Centralising the shared fields here
 * means future additions are a single edit instead of one-per-meta-type.
 *
 * `childScopes` is metadata for the doc walker and a contract hint —
 * the parent op's `apply` is responsible for consulting each scope's
 * `.registry` when dispatching child calls. Eager ops cannot
 * meaningfully introduce child scopes (their args are pre-evaluated);
 * the field is harmlessly ignored if set on them.
 *
 * Typed as `readonly Scope[]` (i.e. `Scope<unknown>[]`): `Scope`'s
 * default `MetaType = unknown` deliberately erases the registry's
 * return type at this boundary so structural assignment from
 * `Scope<VerbMeta>` / `Scope<AggOpMeta>` / etc. works via method
 * return-type covariance — and so MetaType doesn't re-enter `BaseOpMeta`
 * through the registry, which would trip TypeScript's recursive type
 * instantiation depth limit.
 */
export type BaseOpMeta = {
  readonly effect: Effect;
  readonly example?: OpExample;
  readonly childScopes?: readonly Scope[];
  /**
   * Doc-richness (Phase 4c.2) — all optional, additive, rendered by
   * `<ExprInstructions>` when present. Existing ops set none and are
   * unaffected. Deliberately NOT derived from the Zod shape: full
   * per-kwarg signature walking stays deferred (see expr/CLAUDE.md);
   * these are author-supplied hints.
   *
   * - `summary`: one-line tagline rendered as the op's primary
   *   description (falls back to the Zod `.describe()` text if unset).
   * - `typeNote`: an extra usage caveat rendered after the description.
   * - `kwargOrder`: canonical kwarg order, rendered as a flat
   *   `Kwargs: a, b, c` hint (no shape extraction).
   */
  readonly summary?: string;
  readonly typeNote?: string;
  readonly kwargOrder?: readonly string[];
  /**
   * Expression-context hooks. `compileExpr` is the optional
   * expression-context compiler — when the op appears inside a
   * `filter` predicate, `derive` body, or any other expression
   * position, it walks the raw AST and returns a per-row function via
   * `compile`. `inferType` is the parallel hook used by the
   * pre-evaluation type-check pass (§5.8): given the same raw call and
   * a recursive `infer` walker, it returns the op's result
   * `ColumnType`.
   *
   * On `BaseOpMeta` (not just `EagerOpMeta`) so a special form can
   * also opt into expression context — `(col …)` is special (it needs
   * raw AST labels) yet must compile inside `derive`/`filter`. The
   * expression walkers gate purely on the method's presence, not on
   * `evaluation`.
   *
   * `inferType` is stored with both `infer` and the return widened to
   * `unknown` for the `$replace` instantiation-depth reason
   * documented on `EagerOpMeta.apply` — `InferFn`'s recursive walk
   * over the deeply-recursive `ColumnType` exceeds TypeScript's limit
   * when materialised through Zod's registry-type generics. Authors
   * recover precise typing by annotating the arrow's return:
   *   `inferType: (call, infer, path): ColumnType => ...`
   * The dispatcher casts to `ColumnType` before consuming the result.
   */
  readonly compileExpr?: (call: RawOpCall, compile: CompileFn, path: string) => RowFn;
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly inferType?: (call: RawOpCall, infer: any, path: string) => unknown;
};

/**
 * Named, typed registry of ops valid in a specific evaluation context.
 *
 * Generalises the established `opMeta` (top-level) + `pipelineVerbs`
 * (inside `transform`) split: instead of every new context (agg-context
 * inside `aggregate` / `time-bucket :agg`, sort-keys inside `sort`,
 * dashboard tile children inside `dashboard`, …) introducing a parallel
 * registry plus bespoke dispatch + doc carve-outs, each context becomes
 * a `Scope<MetaType>` wrapping its own `z.registry<MetaType>()` with a
 * human-facing `name` + `description` and an authoritative `schemas`
 * list (used by the doc walker and for `createOpTable`-style name
 * tables).
 *
 * Parent ops declare which scopes their children resolve from via the
 * `childScopes` field on `BaseOpMeta`. The doc walker
 * (`ExprInstructions`) starts at the host's root scope (typically
 * `topLevelScope`) and recursively collects all reachable scopes
 * via `meta.childScopes`, emitting each ONCE as an `<h2>` section so
 * multi-parent scopes (e.g. agg-context under both `aggregate` and
 * `time-bucket`) don't duplicate.
 *
 * The runtime dispatch path is unchanged: parent ops still pass
 * `scope.registry` into `resolveOpCall`. Adding a new scope is one
 * `defineScope` call + one `childScopes: [...]` declaration on the
 * parent op — no edits to evaluator, type-checker, or doc generator.
 */
export type Scope<MetaType = unknown> = {
  readonly name: string;
  readonly description?: string;
  // Structural subtype of `z.registry<>()`'s return — just the `.get`
  // method the doc walker + dispatchers consult. Spelling the full
  // `z.$ZodRegistry<MetaType>` here would re-enter Zod's recursive
  // type machinery via `MetaType` (which itself references `Scope`
  // through `childScopes`), tripping TypeScript's instantiation-depth
  // limit. The actual `z.registry<MetaType>()` instance structurally
  // satisfies this shape.
  //
  // Method syntax + `any` parameter is deliberate: method-typed `get`
  // is bivariant in its argument, so `Scope<MetaType>` is assignable to
  // `Scope<unknown>` (the type used on `BaseOpMeta.childScopes` to
  // break the cycle). The default `MetaType = unknown` lets callers
  // omit the generic when using `Scope` as a type-erased reference.
  readonly registry: {
    // biome-ignore lint/suspicious/noExplicitAny: see comment above
    readonly get: (schema: any) => MetaType | undefined;
  };
  // Schemas list typed as `readonly unknown[]` at this base shape so
  // `Scope` can hold heterogeneous `ZodObject<...specific shape...>`
  // instances (every op has its own shape and `as const` arrays carry
  // each specific type; widening to `z.ZodObject` still trips deep
  // structural comparisons through Zod's internal types). The doc
  // walker and `opNameOf` cast back to `z.ZodObject` at access.
  readonly schemas: readonly unknown[];
};

/**
 * Package a registry + its schemas list as a named, documented scope.
 * Asserts every schema is registered in the supplied registry — catches
 * the typo of forgetting `.register(scope.registry, {...})` at module
 * load time rather than at first dispatch.
 *
 * `MetaType` is inferred from the registry's `.get` return type (e.g.
 * passing `pipelineVerbs: z.registry<VerbMeta>()` yields
 * `Scope<VerbMeta>`). Authors don't need to annotate `MetaType` at the
 * call site. The registry parameter is structurally typed (just the
 * `.get` method) to avoid re-entering Zod's recursive type machinery —
 * see the comment on `Scope.registry` for the depth-limit context.
 */
export const defineScope = <MetaType>(opts: {
  readonly name: string;
  readonly description?: string;
  // biome-ignore lint/suspicious/noExplicitAny: see comment on Scope.registry
  readonly registry: { readonly get: (schema: any) => MetaType | undefined };
  readonly schemas: readonly z.ZodObject[];
}): Scope<MetaType> => {
  for (const schema of opts.schemas) {
    if (opts.registry.get(schema) === undefined) {
      throw new Error(
        `defineScope('${opts.name}'): a listed schema is not registered in the scope's registry. ` +
          `Check that the op chains '.register(<registry>, {...})' with the same registry passed here.`,
      );
    }
  }
  return opts;
};
/**
 * Cast a heterogeneous `Scope.schemas` element back to a concrete
 * `z.ZodObject` so it can be looked up in a scope's registry. `Scope.schemas`
 * is intentionally widened to `readonly unknown[]` at the base to keep
 * differently-shaped op schemas assignable across the `childScopes` cycle;
 * every consumer recovers the registry-key type through this single helper.
 */
export const asOpSchema = (schema: unknown): z.ZodObject => schema as z.ZodObject;

/**
 * Walk a root scope transitively via each schema's `meta.childScopes`,
 * collecting every reachable scope in deterministic depth-first order.
 * Dedupes so multi-parent scopes (e.g. agg-context under both `aggregate`
 * and `time-bucket`) appear once — `Set` identity is on the scope object,
 * which is shared by reference across `childScopes` declarations.
 *
 * Single source of truth for scope enumeration: the doc walker
 * (`ExprInstructions`) and the example round-trip test both consume this
 * rather than re-implementing the DFS.
 */
export const collectScopes = (root: Scope<unknown>): readonly Scope<unknown>[] => {
  const seen = new Set<Scope<unknown>>();
  const order: Scope<unknown>[] = [];
  const visit = (scope: Scope<unknown>): void => {
    if (seen.has(scope)) {
      return;
    }
    seen.add(scope);
    order.push(scope);
    for (const schema of scope.schemas) {
      const meta = scope.registry.get(asOpSchema(schema)) as BaseOpMeta | undefined;
      for (const child of meta?.childScopes ?? []) {
        visit(child as Scope<unknown>);
      }
    }
  };
  visit(root);
  return order;
};

// Op metadata.
//
// Apply's INPUT (`data`) uses Zod's `$output` marker. When `opMeta.get(schema)`
// is called, `$output` is substituted with `z.infer<typeof schema>` — so op
// authors writing `apply: ({ params }) => ...` get precise per-schema typing
// without an explicit `z.infer<typeof X>` annotation.
//
// Apply's OTHER ARGS (`ctx`, `dataset`) and RETURN are typed as `any` /
// `unknown` in the registry-stored shape. `$replace` walks every nested
// type when computing the substituted signature, and the engine's types
// hit the instantiation-depth limit on:
//
// - **`Value`** (recursive: `Scalar | readonly Value[] | Dataset`) anywhere
//   in apply's return — e.g. via `EvalResult.value`.
// - **`Dataset`** in arg/return — walks the `Date` class in `Scalar`,
//   which expands into hundreds of prototype methods.
// - **`SpecialCtx`** in arg position — its `evalNode` and `evalVerb`
//   transitively reference `Value` and `Dataset`.
//
// `any`/`unknown` short-circuit `$replace`. Authors recover precise typing
// by annotating the arrow at the call site:
//
//   apply: ({ params }): Value => ...
//   apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => ...
//   apply: ({ params }, dataset: Dataset): Dataset => ...
//
// The dispatcher (`dispatchCall`, `compileExpr`) trusts the declared
// return shape; no runtime check happens beyond the schema's pre-apply
// validation.

/**
 * Eager op metadata. Args are schema-validated and pre-resolved before
 * `apply` runs. The optional expression-context hooks `compileExpr` /
 * `inferType` live on the shared `BaseOpMeta` (a special form can opt
 * into expression context too — see `(col …)`).
 */
export type EagerOpMeta = BaseOpMeta & {
  readonly evaluation: 'eager';
  readonly apply: (data: $output) => unknown;
};

/**
 * Special-form op metadata. Children are received raw; the form controls
 * when (and whether) to evaluate them via `ctx.evalNode` / `ctx.evalVerb`.
 * May return a new env so the caller can thread bindings through subsequent
 * siblings. `ctx` is typed `any` in storage; authors annotate
 * `ctx: SpecialCtx` on the arrow.
 */
export type SpecialOpMeta = BaseOpMeta & {
  readonly evaluation: 'special';
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly apply: (data: $output, ctx: any) => unknown;
};

export type OpMeta = EagerOpMeta | SpecialOpMeta;

/**
 * Op metadata registry. Each registered schema's apply + docs live here;
 * the dispatcher reads via `opMeta.get(schema)`. Zod's `z.registry` (over a
 * plain Map) gives precise per-schema typing of `apply.data` via `$output`,
 * removing the need for `z.infer<typeof X>` casts at each registration.
 */
export const opMeta = z.registry<OpMeta>();
/**
 * Fields shared by every pipeline-verb meta (§5.5 propagation,
 * §5.8 pre-eval arg validation, Phase 4-prep dispatch).
 *
 * `propagateSchema` maps the input dataset's Schema to the output
 * dataset's Schema given the verb's parsed args. The engine assigns
 * the propagated Schema to the verb's output Dataset after apply; if
 * a verb omits `propagateSchema`, the engine falls back to an
 * all-`unknown` schema synthesised from the result table's columns.
 *
 * `referencedColumns` is the verb's declaration of which column names
 * its args USE (definitions like derive's new-column LHS, rename's
 * target, are excluded). Single source of truth for:
 *   - Unknown-column-ref diagnostics in the §5.8 type-check pass
 *     (default impl in `schema/check.ts`'s `validateRefs`).
 *   - Per-cell dispatch axis detection in `evalVerbCall` (Phase 4-prep
 *     sub-task 4-prep.3): a ref into a `nested-dataset` column triggers
 *     automatic per-cell lifting of the verb's `apply`.
 *   - (Future) Push-down optimiser pattern-matching.
 * Verbs without column-ref args (`limit`) omit the field.
 *
 * `validateArgs` is the per-verb hook for any custom diagnostics that
 * go beyond plain unknown-ref checks. The type-check pass aggregates
 * its return value with the default unknown-ref diagnostics derived
 * from `referencedColumns`. Today every verb's column-ref needs are
 * covered by `referencedColumns` alone; this hook is reserved for
 * future verbs whose arg validation requires custom logic.
 *
 * Centralising the fields on a shared base mirrors `BaseOpMeta` —
 * future verb-wide additions are a single edit.
 *
 * `data` keeps Zod's `$output` marker for precise per-schema typing
 * of parsed args. `ctx` and the return are widened to `any`/`unknown`
 * in storage — `PropagateSchemaCtx` / `ValidateArgsCtx` and the
 * returned `Schema` / `readonly ExprError[]` / `readonly ColumnRefInArg[]`
 * each transitively reference recursive types (`ColumnType`, `ExprError`)
 * and exceed TypeScript's instantiation-depth limit when materialised
 * through Zod's registry-type generics (same constraint documented on
 * `EagerOpMeta`'s `apply`/`ctx`). Authors recover precise typing on
 * the arrow:
 *   `propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => …`
 *   `referencedColumns: (data, basePath: string): readonly ColumnRefInArg[] => …`
 *   `validateArgs: (data, ctx: ValidateArgsCtx): readonly ExprError[] => …`
 * The engine trusts the declared return shape and casts to the precise
 * type before consuming the result.
 */
export type BaseVerbMeta = BaseOpMeta & {
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly propagateSchema?: (data: $output, ctx: any) => unknown;
  readonly referencedColumns?: (data: $output, basePath: string) => unknown;
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly validateArgs?: (data: $output, ctx: any) => unknown;
};

/**
 * Eager pipeline-verb metadata. Args are schema-validated and pre-resolved
 * before `apply` runs. `dataset` is typed `any` in storage; authors
 * annotate `dataset: Dataset` on the arrow.
 */
export type EagerVerbMeta = BaseVerbMeta & {
  readonly evaluation: 'eager';
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly apply: (data: $output, dataset: any) => unknown;
};

/**
 * Special pipeline-verb metadata. Args are raw AST; the verb is
 * responsible for compiling them via `ctx.compile`. `dataset` and `ctx`
 * are typed `any` in storage; authors annotate them on the arrow.
 */
export type SpecialVerbMeta = BaseVerbMeta & {
  readonly evaluation: 'special';
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  readonly apply: (data: $output, dataset: any, ctx: any) => unknown;
};

export type VerbMeta = EagerVerbMeta | SpecialVerbMeta;

/**
 * Pipeline-verb registry, parallel to `opMeta`. Verbs are only dispatched
 * by `transform`'s apply — they are not visible to the top-level
 * evaluator, so calling a verb name at top level surfaces
 * `UnknownOpError`, not `UnknownVerbError`.
 */
export const pipelineVerbs = z.registry<VerbMeta>();
/**
 * Aggregation compile spec — an intermediate representation produced by
 * agg-context funcs (`sum`, `mean`, `count`, …) and consumed by the
 * `aggregate` / `time-bucket :agg` verbs. Discriminated by `kind` so
 * the §8.1 arquero boundary (only `runtime/arquero.ts` imports arquero)
 * stays intact: agg funcs declare WHAT aggregation to perform without
 * referencing arquero ops directly; `runtime/arquero.ts` translates the
 * spec into a concrete arquero rollup expression.
 *
 * Variants:
 *
 *   - `col-agg` — standard column aggregation (sum/mean/min/max/first/last).
 *   - `row-count` — zero-arg row count (`(count)`).
 *   - `col-array` — collect cells of `col` into an array (`(values …)`,
 *     `(timestamps)`).
 *   - `col-pick` — pick a single cell of `col`, defaulting to the last
 *     (`(value)` — TS-ref convenience for "latest value").
 */
export type AggSpec =
  | { readonly kind: 'col-agg'; readonly op: ArqueroAggOpName; readonly col: string }
  | { readonly kind: 'row-count' }
  | { readonly kind: 'col-array'; readonly col: string }
  | { readonly kind: 'col-pick'; readonly col: string; readonly pick: 'first' | 'last' };

/** Arquero `op.*` names supported by `col-agg`. Subset of arquero's surface that we expose. */
export type ArqueroAggOpName = 'sum' | 'mean' | 'min' | 'max' | 'first' | 'last';

/**
 * Compile callback handed to agg-context func registrations. Receives
 * the raw `OpCall` for the func (so it can read its args — typically a
 * single column-symbol arg) and the dispatch path for diagnostics;
 * returns an `AggSpec` describing the aggregation to perform.
 *
 * Agg funcs are pure — they do not need an env / session context, and
 * they never trigger I/O. Compilation is deterministic and side-effect-
 * free.
 */
export type AggCompileFn = (call: RawOpCall, path: string) => AggSpec;

/**
 * Agg-context op metadata. Used by funcs callable INSIDE `aggregate`
 * (positional `(out-name agg-call)` pairs) or `time-bucket :agg`. The
 * funcs are NOT registered in `opMeta`, so calling one at top level
 * surfaces `UnknownOpError` — context restriction is enforced by
 * registry separation, same pattern as `pipelineVerbs` vs `opMeta`.
 *
 * `compileAggExpr` returns an `AggSpec`; the parent verb's apply
 * collects the specs into a rollup object and hands off to
 * `runtime/arquero.ts` for translation to an actual arquero call.
 *
 * Optional `inferType` participates in the pre-eval type-check pass —
 * same shape as `EagerOpMeta.inferType` but applied inside the
 * aggregate context where the per-row schema is the source dataset's
 * schema (or the inner schema, during per-cell lifting on TS-ref
 * columns).
 */
export type AggOpMeta = BaseOpMeta & {
  readonly compileAggExpr: AggCompileFn;
  // biome-ignore lint/suspicious/noExplicitAny: same $replace-depth ceremony as EagerOpMeta.inferType
  readonly inferType?: (call: RawOpCall, infer: any, path: string) => unknown;
};

/**
 * Agg-context registry, parallel to `opMeta` and `pipelineVerbs`. Agg
 * funcs are registered here ONLY — the parent verbs (`aggregate`,
 * `time-bucket`) dispatch through this registry directly. Listing a
 * func here without also packaging it in `aggContextScope` (in
 * `ops/transform/agg-funcs.ts`) is a programming error; `defineScope`
 * asserts the cross-link at module load.
 */
export const aggMeta = z.registry<AggOpMeta>();

/**
 * Dispatch table: `name` → schema. Built once per evaluation context from
 * the composed list of registered schemas. O(1) dispatch by `name`. Used
 * for both top-level ops (via `createOpTable`) and pipeline verbs (via
 * `createVerbTable`) — the namespaces are separate registry *instances*
 * (`opMeta` vs `pipelineVerbs`), but they share the same table shape.
 */
export type NameTable = ReadonlyMap<string, z.ZodObject>;

/**
 * Extract the literal value of an op schema's `name` field. Every op schema
 * must declare `name: z.literal('...')` so the dispatcher can index it.
 */
export const opNameOf = (schema: z.ZodObject): string => {
  const nameField = schema.shape.name as unknown;
  if (nameField === undefined || nameField === null) {
    throw new Error('op schema is missing required `name` field');
  }
  const lit = (nameField as { def?: { values?: readonly unknown[] } }).def;
  const values = lit?.values;
  if (!Array.isArray(values) || values.length !== 1 || typeof values[0] !== 'string') {
    throw new Error('op schema `name` must be `z.literal(<string>)`');
  }
  return values[0];
};

/**
 * Shared `name → schema` table builder. The `label` parameter is folded
 * into the duplicate-registration error message so the failure points at
 * the right registry (e.g. "duplicate op registration: +").
 */
const buildNameTable = (schemas: readonly z.ZodObject[], label: string): NameTable => {
  const entries = schemas.map((s) => [opNameOf(s), s] as const);
  const names = entries.map(([n]) => n);
  const dupe = names.find((n, i) => names.indexOf(n) !== i);
  if (dupe !== undefined) {
    throw new Error(`duplicate ${label} registration: ${dupe}`);
  }
  return new Map(entries);
};

/**
 * Build a `NameTable` from a list of op schemas. Duplicate names are a
 * hard error — the table is a single namespace.
 */
export const createOpTable = (schemas: readonly z.ZodObject[]): NameTable => buildNameTable(schemas, 'op');

/** Build a `NameTable` from a list of pipeline-verb schemas. */
export const createVerbTable = (schemas: readonly z.ZodObject[]): NameTable => buildNameTable(schemas, 'pipeline verb');
/**
 * Resolve an op-call to its `(schema, meta)` pair. Looks `name` up in
 * `table`, instantiates `notFound` and throws it if missing; then reads
 * `meta` from `registry`, throws an internal `Error` if the registry has
 * no entry (which only happens if a schema reaches the table without
 * being registered — a programmer bug).
 *
 * Shared between the evaluator's `dispatchCall` and `runtime/arquero.ts`'s
 * `compileExpr`. The `notFound` class is supplied by the caller so each
 * dispatch surface throws its kind-specific error (`UnknownOpError`,
 * `UnknownVerbError`).
 */
export const resolveOpCall = <MetaType>(
  name: string,
  table: NameTable,
  registry: { readonly get: (schema: z.ZodType) => MetaType | undefined },
  pos: SourcePos,
  metaLabel: string,
  notFound: new (name: string, available: readonly string[], pos: SourcePos) => ExprError,
): { readonly schema: z.ZodObject; readonly meta: MetaType } => {
  const schema = table.get(name);
  if (schema === undefined) {
    throw new notFound(name, [...table.keys()], pos);
  }
  const meta = registry.get(schema);
  if (meta === undefined) {
    throw new Error(`internal: ${metaLabel} '${name}' has no registered meta`);
  }
  return { schema, meta };
};
